import React from 'react';
import { Eye, Edit2, Trash2, CircleCheckBig, CircleAlert } from 'lucide-react';

const EquipmentTable = ({ equipment, activeTab, styles }) => {
  const filteredEquipment = equipment.filter(item => {
    if (activeTab === 'all') return true;
    if (activeTab === 'overdue') return item.status === 'overdue';
    if (activeTab === 'certificates') return item.type === 'LSA' || item.type === 'FFE';
    return true;
  });

  const getStatusBadge = (status) => {
    // Check if this is a FleetSkipper v2 variant (has badge styling properties)
    const useDrillsStyle = styles.badgeDisplay || styles.id === 'seareadyv2';

    // Badge configurations matching Drills module style
    const statusConfig = {
      overdue: {
        label: 'OVERDUE',
        bg: styles.badgeOverdueBg || 'bg-red-100',
        text: styles.badgeOverdueText || 'text-red-600',
        icon: CircleAlert,
        // Legacy fallback
        classes: `bg-red-100 ${styles.statusColors?.overdue || 'text-red-700'}`,
        richClasses: 'bg-red-900 text-white',
      },
      due_soon: {
        label: 'DUE SOON',
        bg: styles.badgeWarningBg || 'bg-yellow-100',
        text: styles.badgeWarningText || 'text-amber-600',
        icon: CircleAlert,
        // Legacy fallback
        classes: `bg-yellow-100 ${styles.statusColors?.warning || 'text-yellow-700'}`,
        richClasses: 'bg-yellow-900 text-white',
      },
      compliant: {
        label: 'COMPLIANT',
        bg: styles.badgeSafeBg || 'bg-green-100',
        text: styles.badgeSafeText || 'text-green-600',
        icon: CircleCheckBig,
        // Legacy fallback
        classes: `bg-green-100 ${styles.statusColors?.compliant || 'text-green-700'}`,
        richClasses: 'bg-green-900 text-white',
      },
    };

    const config = statusConfig[status] || statusConfig.compliant;

    // Use Drills-style badges with icons for FleetSkipper v2
    if (useDrillsStyle) {
      const IconComponent = config.icon;
      return (
        <span className={`
          ${styles.badgeDisplay || 'inline-flex items-center'}
          ${styles.badgePadding || 'py-0.5 px-2'}
          ${styles.badgeRounded || 'rounded-full'}
          ${styles.badgeSize || 'text-xs'}
          ${styles.badgeWeight || 'font-medium'}
          ${config.bg}
          ${config.text}
        `}>
          {/* Icon on LEFT - Drills module style */}
          <IconComponent
            className={`${styles.badgeIconSize || 'h-3 w-3'} ${styles.badgeIconMargin || 'mr-1'}`}
            strokeWidth={2}
          />
          <span>{config.label}</span>
        </span>
      );
    }

    // Legacy badge styling for other variants
    const badgeClasses = styles.id === 'rich' && styles.statusBadgeBg
      ? config.richClasses
      : config.classes;

    return (
      <span className={`px-2 py-1 ${styles.rounded} ${styles.tableHeaderSize || 'text-xs'} ${styles.buttonTextWeight || styles.fontWeight} ${badgeClasses}`}>
        {config.label}
      </span>
    );
  };

  return (
    <div className={`bg-white ${styles.rounded} ${styles.shadow} border ${styles.borderColor} overflow-hidden`}>
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            {/* Table headers - use tableHeaderWeight/Size/Color */}
            <th className={`px-6 py-3 text-left ${styles.tableHeaderSize || 'text-xs'} ${styles.tableHeaderWeight || styles.fontWeight} ${styles.tableHeaderColor || 'text-gray-500'} uppercase ${styles.tracking || ''}`}>
              Equipment
            </th>
            <th className={`px-6 py-3 text-left ${styles.tableHeaderSize || 'text-xs'} ${styles.tableHeaderWeight || styles.fontWeight} ${styles.tableHeaderColor || 'text-gray-500'} uppercase ${styles.tracking || ''}`}>
              Type
            </th>
            <th className={`px-6 py-3 text-left ${styles.tableHeaderSize || 'text-xs'} ${styles.tableHeaderWeight || styles.fontWeight} ${styles.tableHeaderColor || 'text-gray-500'} uppercase ${styles.tracking || ''}`}>
              Status
            </th>
            <th className={`px-6 py-3 text-left ${styles.tableHeaderSize || 'text-xs'} ${styles.tableHeaderWeight || styles.fontWeight} ${styles.tableHeaderColor || 'text-gray-500'} uppercase ${styles.tracking || ''}`}>
              Next Service
            </th>
            <th className={`px-6 py-3 text-left ${styles.tableHeaderSize || 'text-xs'} ${styles.tableHeaderWeight || styles.fontWeight} ${styles.tableHeaderColor || 'text-gray-500'} uppercase ${styles.tracking || ''}`}>
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {filteredEquipment.map(item => {
            const IconComponent = item.icon;
            return (
              <tr key={item.id} className={`hover:bg-gray-50 transition cursor-pointer ${styles.tableRowHeight || ''}`}>
                <td className={`${styles.tableCellPadding || 'px-6 py-4'} whitespace-nowrap`}>
                  <div className="flex items-center">
                    <IconComponent
                      className={`${styles.iconSize} ${styles.iconColor} mr-3`}
                      strokeWidth={styles.strokeWidth}
                    />
                    {/* Equipment name - PRIMARY identifier (font-medium) */}
                    <span className={`${styles.tableTextSize || styles.bodyTextSize || styles.fontSize} ${styles.equipmentTitleWeight || 'font-medium'} ${styles.tableTextColor || 'text-gray-900'}`}>
                      {item.name}
                    </span>
                  </div>
                </td>
                <td className={`${styles.tableCellPadding || 'px-6 py-4'} whitespace-nowrap ${styles.tableTextSize || styles.bodyTextSize || styles.fontSize} ${styles.tableTextColor || 'text-gray-500'}`}>
                  {item.type}
                </td>
                <td className={`${styles.tableCellPadding || 'px-6 py-4'} whitespace-nowrap`}>
                  {getStatusBadge(item.status)}
                </td>
                <td className={`${styles.tableCellPadding || 'px-6 py-4'} whitespace-nowrap ${styles.tableTextSize || styles.bodyTextSize || styles.fontSize} ${styles.statusColor}`}>
                  {item.nextService}
                </td>
                <td className={`${styles.tableCellPadding || 'px-6 py-4'} whitespace-nowrap`}>
                  <div className="flex gap-2">
                    <button className="p-1 hover:bg-gray-100 rounded transition">
                      <Eye className="h-4 w-4 text-gray-600" />
                    </button>
                    <button className="p-1 hover:bg-gray-100 rounded transition">
                      <Edit2 className="h-4 w-4 text-gray-600" />
                    </button>
                    <button className="p-1 hover:bg-gray-100 rounded transition">
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default EquipmentTable;
